import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  Video,
  Building2,
  CheckCircle2,
  Clock,
  Stethoscope,
  FileCheck,
  MapPin,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import visitHero from "@/assets/visit-hero.jpg";
import { PageMeta } from "@/components/site/PageMeta";

const InforBesoket = () => {
  const { t, lang } = useLang();


  const title =
    lang === "sv"
      ? "Inför besöket | Intygsläkaren"
      : "Before your visit | Intygsläkaren";
  const desc =
    lang === "sv"
      ? "Allt du behöver veta inför ditt besök hos Intygsläkaren — videomöte eller klinikbesök i Göteborg."
      : "Everything you need to know before your visit to Intygsläkaren — video or in-person at our Gothenburg clinic.";

  const contactHref = lang === "sv" ? "/kontakt" : "/en/contact";
  const certificatesHref = lang === "sv" ? "/lakarintyg" : "/en/lakarintyg";
  const v = t.visitPage;

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={title} description={desc} />
      <Header />
      <main>
        {/* Hero */}
        <PageHeroCard imageSrc={visitHero} imageAlt={lang === "sv" ? "Klinikens reception" : "Clinic reception"} compact>
          <div className="animate-fade-up max-w-2xl">
            <h1 className="text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {v.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {v.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="xl" variant="hero">
                <Link to={certificatesHref}>{t.hero.browseCta}</Link>
              </Button>
              <Button asChild size="xl" variant="ghost-outline">
                <Link to={contactHref}>{v.contactCta}</Link>
              </Button>
            </div>
          </div>
        </PageHeroCard>

        {/* Visit options */}
        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {v.optionsTitle}
              </h2>
              <p className="mt-4 text-base text-muted-foreground">{v.optionsSubtitle}</p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-2">
              {/* Video */}
              <div className="flex flex-col rounded-2xl border border-border bg-card sm:rounded-3xl p-8 shadow-card">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Video className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground">
                    {v.videoTitle}
                  </h3>
                </div>
                <p className="mt-4 text-muted-foreground">{v.videoText}</p>
                <ul className="mt-6 space-y-3">
                  {v.videoBullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Clinic */}
              <div className="flex flex-col rounded-2xl border border-border bg-card sm:rounded-3xl p-8 shadow-card">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground">
                    {v.clinicTitle}
                  </h3>
                </div>
                <p className="mt-4 text-muted-foreground">{v.clinicText}</p>
                <ul className="mt-6 space-y-3">
                  {v.clinicBullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* What to bring */}
        <section className="pb-14 lg:pb-20">
          <div className="container-wide">
            <div className="mx-auto max-w-4xl rounded-2xl border border-border bg-card sm:rounded-3xl p-8 shadow-card lg:p-12">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {v.bringTitle}
              </h2>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {v.bringItems.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground sm:text-base">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* During the visit */}
        <section className="pb-14 lg:pb-20">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {v.duringTitle}
              </h2>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {[
                { icon: Clock, title: v.durationTitle, text: v.durationText },
                { icon: Stethoscope, title: v.examTitle, text: v.examText },
                { icon: FileCheck, title: v.issueTitle, text: v.issueText },
              ].map(({ icon: Icon, title, text }) => (
                <div key={title} className="rounded-2xl border border-border bg-card sm:rounded-3xl p-8 shadow-card">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold text-foreground">{title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ready CTA */}
        <section className="px-2 py-16 sm:px-3 lg:px-4 lg:py-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:px-16 lg:py-20">
              <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
              <div className="relative max-w-2xl">
                <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                  {v.readyTitle}
                </h2>
                <p className="mt-3 text-base text-primary-foreground/85">{v.readyText}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild variant="ghostOnPrimary" size="xl">
                    <a href="tel:+46317617700">
                      <Phone className="mr-2 h-4 w-4" />
                      {t.cta.secondary}
                    </a>
                  </Button>
                  <Button asChild variant="ghostOnPrimary" size="xl">
                    <Link to={contactHref}>
                      <Mail className="mr-2 h-4 w-4" />
                      {v.contactCta}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
        </section>
        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: t.nav.visit },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default InforBesoket;